import type { ApprovalMode, ClFarmAlmType, EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import type { Address, PublicClient } from "viem"
import type { Config } from "wagmi"
import { ClFarmStore } from "@/domains/cl-farm"
import { approveOwnerSpenderAllowance, mintIchiClFarmLiquidity } from "@/domains/on-chain"
import { TokenStore } from "@/domains/tokens"
import { erc20Abi } from "viem"

type MintClFarmLiquidityInteraction = {
  connectedAddress: Address
  amount: bigint
  approvalMode: ApprovalMode
  chainId: number
  clFarm: ClFarmStore
  config: Config
  emitter: EventEmitter<EmitEvent>
  mintToken0: TokenStore
  publicClient: PublicClient
  onSuccess: () => void
}

export const mintIchiClFarmLiquidityInteraction = async ({
  connectedAddress,
  amount,
  approvalMode,
  chainId,
  clFarm,
  config,
  emitter,
  mintToken0,
  publicClient,
  onSuccess,
}: MintClFarmLiquidityInteraction) => {
  const guardAddress = clFarm?.guardAddress?.toViemAddress()
  const ichiTokenAddress = clFarm.providerLp.address.toViemAddress()
  const vdepAddress = clFarm.vdep as Address

  if (!guardAddress) {
    return emitter.emit("error-invalid-cl-farm", {
      title: "Invalid CL Farm",
      description: "The CL Farm guard address is invalid. Please try again.",
      variant: "error",
    })
  }
  if (!ichiTokenAddress) {
    return emitter.emit("error-invalid-cl-farm", {
      title: "Invalid CL Farm",
      description: "The CL Farm ichi token address is invalid. Please try again.",
      variant: "error",
    })
  }
  if (!vdepAddress) {
    return emitter.emit("error-invalid-cl-farm", {
      title: "Invalid CL Farm",
      description: "The CL Farm vdep address is invalid. Please try again.",
      variant: "error",
    })
  }

  //#region Defensive checks
  if (!connectedAddress) {
    return emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
  }
  //#endregion

  //#region STEP ONE: Check if the user has enough balance in their wallet
  if (mintToken0.balance < amount) {
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You do not have enough ${mintToken0.symbol} in your wallet. You are trying to mint ${mintToken0.formatUnits(amount - mintToken0.balance)} more than available.`,
      variant: "error",
    })
    return
  }
  //#endregion

  //#region STEP TWO - Does the user need to provide an allowance?
  let allowance = await publicClient.readContract({
    address: mintToken0.address.toViemAddress(),
    abi: erc20Abi,
    functionName: "allowance",
    args: [connectedAddress, guardAddress],
  })

  if (allowance < amount) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${mintToken0.formatUnits(amount)} ${mintToken0.symbol}`,
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account: connectedAddress,
      chainId,
      config,
      contractAddress: mintToken0.address.toViemAddress(),
      spenderAddress: guardAddress,
      spenderAmount: amount,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", {
        title: "Approval successful",
        description: "You may now proceed to mint",
        variant: "success",
      })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error ?? "unknown error",
        variant: "error",
      })
      return
    }
  }
  //#endregion

  allowance = await publicClient.readContract({
    address: mintToken0.address.toViemAddress(),
    abi: erc20Abi,
    functionName: "allowance",
    args: [connectedAddress, guardAddress],
  })

  //#region STEP THREE: Mint the CL Farm Liquidity
  const mintClFarmLiquidityResult = await mintIchiClFarmLiquidity({
    account: connectedAddress,
    chainId,
    config,
    guardAddress,
    mintToken0Address: mintToken0.address.toViemAddress(),
    ichiTokenAddress,
    vdepAddress,
    amount,
  })

  if (mintClFarmLiquidityResult.success) {
    onSuccess?.()
    emitter.emit("success-mint-cl-farm-liquidity", {
      title: `Mint ${clFarm.displayName}`,
      description: `Minted ${clFarm.displayName} tokens with ${mintToken0.formatUnits(amount)} ${mintToken0.symbol}`,
      variant: "success",
    })
  } else {
    emitter.emit("error-mint-cl-farm-liquidity", {
      title: `Unable to mint ${mintToken0.formatUnits(amount)} ${clFarm.displayName}`,
      description: mintClFarmLiquidityResult.error || "Unknown error",
      variant: "error",
    })
  }
  //#endregion

  return mintClFarmLiquidityResult
}
